import { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Button, Stack, Alert, Typography,
} from "@mui/material";
import { updateBillingCompany, type Emitter } from "../services/billing";

interface Props {
  open:       boolean;
  period:     string;                 // 'YYYYMMDD-YYYYMMDD'
  collector:  string;
  cuit:       string;
  /** Empresa actual de la row (string de invoice_ar). */
  current:    string | null | undefined;
  /** ¿Ya venía editada a mano? Cambia el texto de la advertencia. */
  wasManual:  boolean;
  emitters:   Emitter[];
  onClose:    () => void;
  onSaved:    (billingCompany: string) => void;
}

/**
 * Cambia qué empresa factura una row del período.
 *
 * El alcance es deliberadamente chico: escribe SOLO la row de invoice_ar de este
 * período y la marca como editada a mano para que "Empresas ⟳" no la pise. El
 * maestro (collection_config) no se toca — la lambda lo tiene read-only — así
 * que si el collector está mal clasificado de origen, el mes que viene vuelve a
 * entrar mal. Eso se avisa en el diálogo para que nadie lo dé por corregido.
 */
export default function BillingCompanyDialog({
  open, period, collector, cuit, current, wasManual, emitters, onClose, onSaved,
}: Props) {
  const [cuitSel, setCuitSel] = useState<number | "">("");
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState<string | null>(null);

  // Matchea la empresa actual contra el emisor por razón social, con la misma
  // normalización laxa del backend (_row_billing_emitter ignora puntos y case).
  useEffect(() => {
    if (!open) return;
    setErr(null);
    const norm = (s: string) => s.replace(/\./g, "").replace(/\s+/g, " ").trim().toLowerCase();
    const actual = norm(current || "");
    const hit = emitters.find((e) => norm(e.razon_social) === actual)
      // "Transferencias SAS" (lo que guarda invoice_ar) vs "TRANSFERENCIAS S.A.S."
      ?? emitters.find((e) => norm(e.razon_social).startsWith(actual.replace(/ sas$/, "")))
      ?? emitters[0];
    setCuitSel(hit?.cuit ?? "");
  }, [open, current, emitters]);

  async function onSave() {
    if (cuitSel === "") return;
    setBusy(true); setErr(null);
    try {
      const r = await updateBillingCompany(period, collector, Number(cuitSel));
      onSaved(r.billing_company);
      onClose();
    } catch (e: any) {
      setErr(e.message ?? "No se pudo cambiar la empresa");
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Empresa que factura
        <Typography variant="body2" color="text.secondary">
          {collector} — CUIT {cuit}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField
            select
            label="Emisor"
            value={cuitSel}
            onChange={(e) => setCuitSel(Number(e.target.value))}
            fullWidth
          >
            {emitters.map((e) => (
              <MenuItem key={e.cuit} value={e.cuit}>
                {e.razon_social}
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  (CUIT {e.cuit})
                </Typography>
              </MenuItem>
            ))}
          </TextField>

          <Alert severity="info" variant="outlined">
            El cambio aplica <strong>solo a este período</strong> y la fila queda marcada
            como editada a mano, así que <em>Empresas ⟳</em> no la va a pisar.
            {wasManual
              ? " Esta fila ya había sido editada a mano."
              : " El maestro de la BD no se modifica: si el collector está mal clasificado de origen," +
                " el mes que viene vuelve a entrar igual y hay que corregirlo upstream."}
          </Alert>

          {err && <Alert severity="error">{err}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancelar</Button>
        <Button onClick={onSave} variant="contained" disabled={busy || cuitSel === ""}>
          {busy ? "Guardando…" : "Guardar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
